
// Import the supabase client that's already set up in your project
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ExtractedInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  skills?: string[];
  summary?: string;
  linkedin?: string;
}

type ProgressCallback = (progress: number, status: string) => void;

// Add this sanitization function near the top of the file
const sanitizeExtractedInfo = (info: any) => {
  if (!info) return null;
  
  // Create a new object with only the properties we need
  return {
    firstName: info.firstName || "",
    lastName: info.lastName || "",
    email: info.email || "",
    phone: info.phone || "",
    role: info.role || "",
    skills: Array.isArray(info.skills) ? [...info.skills] : [],
    summary: info.summary ? String(info.summary) : "",
    linkedin: info.linkedin || ""
  };
};

/**
 * Extracts candidate information from a CV file
 * Using the quick-scan-candidate edge function for enhanced extraction
 */
export const extractInfoFromCV = async (
  file: File, 
  onProgress?: ProgressCallback
): Promise<ExtractedInfo> => {
  // Initialize the extracted info object
  const extractedInfo: ExtractedInfo = {};

  console.log("Extracting information from CV...");
  onProgress?.(10, "Starting CV processing");

  try {
    // Read the file content using edge function
    onProgress?.(20, "Extracting text from document");
    const fileContent = await extractTextFromFile(file);

    if (!fileContent || fileContent.length < 10) {
      console.warn("Warning: File content is empty or too short");
      return extractedInfo;
    }

    console.log(`CV content extracted, length: ${fileContent.length}`);
    onProgress?.(50, "Text extraction completed");

    // Use the quick-scan-candidate edge function to extract information from the CV
    try {
      console.log("Calling quick-scan-candidate function to analyze CV...");
      onProgress?.(60, "Analyzing CV contents");
      
      const { data, error } = await supabase.functions.invoke('quick-scan-candidate', {
        body: { cvText: fileContent }
      });

      if (error) {
        console.error("Error from quick-scan-candidate function:", error);
        throw new Error(`Quick scan failed: ${error.message}`);
      }

      if (!data) {
        console.warn("No data returned from quick-scan-candidate function");
        return extractedInfo;
      }

      console.log("Quick scan results:", data);
      onProgress?.(90, "Analysis completed");

      // Map the response to our expected format
      if (data.firstName) extractedInfo.firstName = data.firstName;
      if (data.lastName) extractedInfo.lastName = data.lastName;
      if (data.email) extractedInfo.email = data.email;
      if (data.phone) extractedInfo.phone = data.phone;
      if (data.desiredRole) extractedInfo.role = data.desiredRole;
      if (data.skills && Array.isArray(data.skills)) extractedInfo.skills = data.skills;
      if (data.summary) extractedInfo.summary = data.summary;
      
      onProgress?.(100, "CV processing completed");
    } catch (scanError) {
      console.error("Error analyzing CV with quick-scan-candidate:", scanError);
      toast({
        title: "CV Analysis Failed",
        description: "Unable to extract information from CV",
        variant: "destructive"
      });
      
      // If quick-scan-candidate fails, we'll return the empty extractedInfo object
    }

  } catch (error) {
    console.error("Error extracting CV information:", error);
    onProgress?.(100, "Error during processing");
  }

  console.log("Extracted info summary:", extractedInfo);
  // Return sanitized object to prevent circular references
  return sanitizeExtractedInfo(extractedInfo);
};

/**
 * Uses the get-document-content Edge Function to read and extract file content
 * This leverages the serverless function's advanced document parsing capabilities
 * 
 * This is the missing function that needs to be exported
 */
export const extractTextFromFile = async (file: File): Promise<string> => {
  try {
    console.log(`Processing file: ${file.name} (${file.type})`);
    
    // Verify user is authenticated before making the request
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.warn("User not authenticated - this may cause function invocation to fail");
    } else {
      console.log("User authenticated, proceeding with function call");
    }
    
    // Convert file to base64
    const base64 = await fileToBase64(file);
    
    // Get file type without the dot
    const fileType = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileType) {
      throw new Error("Could not determine file type");
    }
    
    // Advanced error handling and better logging
    try {
      console.log("Invoking edge function with file type:", fileType);
      
      // Replace fetch with supabase.functions.invoke
      const { data: result, error } = await supabase.functions.invoke('get-document-content', {
        body: {
          documentBase64: base64,
          fileType: fileType
        }
      });
      
      console.log("Edge function response:", result);
      
      if (error) {
        console.error("Function error details:", error);
        toast({
          title: "Document Processing Failed",
          description: error?.message || "Unable to process document",
          variant: "destructive"
        });
        throw new Error(error?.message || "Edge function failed");
      }
      
      if (!result?.text) {
        throw new Error("No text content returned from edge function");
      }
      
      return result.text;

    } catch (functionError: any) {
      // Check specific error conditions that might indicate auth issues
      const errorMsg = functionError?.message || "Unknown error";
      
      if (errorMsg.includes("unauthorized") || errorMsg.includes("JWT")) {
        console.error("Authentication error with function:", errorMsg);
        toast({
          title: "Authentication Error",
          description: "You may need to log in again to process documents",
          variant: "destructive" 
        });
      } else {
        console.error("Function invocation error:", errorMsg);
      }
      
      throw functionError;
    }
  } catch (error: any) {
    console.error("Error in edge function document processing:", error);
    
    // Fallback for PDF files - at least use filename as basic info
    if (file.type === 'application/pdf') {
      const fileName = file.name.replace('.pdf', '').replace(/[_-]/g, ' ');
      return fileName + "\n" + "PDF Content: Unable to extract full text content from PDF file.";
    }
    
    throw error;
  }
};

/**
 * Helper function to read file content directly using FileReader
 * Used as a fallback if the edge function fails
 */
const readFileDirectly = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("FileReader did not return a string"));
      }
    };
    reader.onerror = () => {
      reject(new Error("Error reading file"));
    };
    reader.readAsText(file);
  });
};

/**
 * Helper function to convert File to base64 string
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error("FileReader did not return a string"));
      }
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Uses the get-document-content Edge Function to read and extract file content
 * This leverages the serverless function's advanced document parsing capabilities
 */
const readFileUsingEdgeFunction = async (file: File): Promise<string> => {
  try {
    console.log(`Processing file: ${file.name} (${file.type})`);
    
    // Verify user is authenticated before making the request
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.warn("User not authenticated - this may cause function invocation to fail");
    } else {
      console.log("User authenticated, proceeding with function call");
    }
    
    // Convert file to base64
    const base64 = await fileToBase64(file);
    
    // Get file type without the dot
    const fileType = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileType) {
      throw new Error("Could not determine file type");
    }
    
    // Advanced error handling and better logging
    try {
      console.log("Invoking edge function with file type:", fileType);
      
      // Replace fetch with supabase.functions.invoke
      const { data: result, error } = await supabase.functions.invoke('get-document-content', {
        body: {
          documentBase64: base64,
          fileType: fileType
        }
      });
      
      console.log("Edge function response:", result);
      
      if (error) {
        console.error("Function error details:", error);
        toast({
          title: "Document Processing Failed",
          description: error?.message || "Unable to process document",
          variant: "destructive"
        });
        throw new Error(error?.message || "Edge function failed");
      }
      
      if (!result?.text) {
        throw new Error("No text content returned from edge function");
      }
      
      return result.text;

    } catch (functionError: any) {
      // Check specific error conditions that might indicate auth issues
      const errorMsg = functionError?.message || "Unknown error";
      
      if (errorMsg.includes("unauthorized") || errorMsg.includes("JWT")) {
        console.error("Authentication error with function:", errorMsg);
        toast({
          title: "Authentication Error",
          description: "You may need to log in again to process documents",
          variant: "destructive" 
        });
      } else {
        console.error("Function invocation error:", errorMsg);
      }
      
      throw functionError;
    }
  } catch (error: any) {
    console.error("Error in edge function document processing:", error);
    
    // Fallback for PDF files - at least use filename as basic info
    if (file.type === 'application/pdf') {
      const fileName = file.name.replace('.pdf', '').replace(/[_-]/g, ' ');
      return fileName + "\n" + "PDF Content: Unable to extract full text content from PDF file.";
    }
    
    throw error;
  }
};
